import { motion, AnimatePresence } from 'framer-motion'
import { X, Rocket, Calendar, Smartphone, Star, CheckCircle, Sparkles } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

interface AppDownloadModalProps {
  isOpen: boolean
  onClose: () => void
}

const AppDownloadModal = ({ isOpen, onClose }: AppDownloadModalProps) => {
  const { t } = useLanguage()

  const scrollToWaitlist = () => {
    onClose()
    setTimeout(() => {
      const element = document.getElementById('waitlist')
      if (element) {
        const offset = 80
        const elementPosition = element.getBoundingClientRect().top
        const offsetPosition = elementPosition + window.pageYOffset - offset
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        })
      }
    }, 300)
  }

  const benefits = [
    {
      icon: Star,
      title: t('appModal.benefit1'),
    },
    {
      icon: Sparkles,
      title: t('appModal.benefit2'),
    },
    {
      icon: CheckCircle,
      title: t('appModal.benefit3'),
    },
  ]

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
          />

          <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden border border-white/10 my-8"
            >
              {/* Header */}
              <div className="sticky top-0 z-10 bg-gradient-to-r from-red-600/20 to-orange-600/20 border-b border-white/10 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <Rocket className="h-8 w-8 text-red-400" />
                      <h2 className="text-2xl md:text-3xl font-bold text-white">
                        {t('appModal.title')}
                      </h2>
                    </div>
                    <p className="text-gray-400 text-sm">{t('appModal.subtitle')}</p>
                  </div>
                  <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors">
                    <X className="h-6 w-6 text-gray-400" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="overflow-y-auto px-6 py-8 max-h-[calc(90vh-100px)]">
                {/* Launch Info */}
                <div className="mb-8">
                  <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 rounded-2xl p-6 border border-red-500/20">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-red-500/20 rounded-xl">
                        <Calendar className="h-6 w-6 text-red-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-white mb-2">{t('appModal.launchTimeline')}</h3>
                        <p className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-400 mb-3">
                          {t('appModal.launchDate')}
                        </p>
                        <p className="text-gray-300 leading-relaxed">{t('appModal.launchInfo')}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Platform Availability */}
                <div className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Smartphone className="h-6 w-6 text-red-400" />
                    <h3 className="text-xl font-bold text-white">{t('appModal.platforms')}</h3>
                  </div>
                  
                  <p className="text-gray-300 mb-6">{t('appModal.platformsDescription')}</p>
                  
                  {/* Platform Badges (non-clickable, styled in gray) */}
                  <div className="flex gap-4 justify-center">
                    <div className="bg-white/5 rounded-2xl px-8 py-4 border border-white/10 flex items-center gap-3 opacity-60">
                      <svg className="h-8 w-8" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                      </svg>
                      <div>
                        <div className="text-sm text-gray-400">Download on</div>
                        <div className="text-white font-semibold">App Store</div>
                      </div>
                    </div>
                    
                    <div className="bg-white/5 rounded-2xl px-8 py-4 border border-white/10 flex items-center gap-3 opacity-60">
                      <svg className="h-8 w-8" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                      </svg>
                      <div>
                        <div className="text-sm text-gray-400">Get it on</div>
                        <div className="text-white font-semibold">Google Play</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Early Access Benefits */}
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Star className="h-6 w-6 text-red-400" />
                    {t('appModal.earlyAccess')}
                  </h3>
                  
                  <div className="space-y-3">
                    {benefits.map((benefit, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.1 }}
                        className="bg-white/5 rounded-xl p-4 border border-red-500/20 flex items-center gap-4"
                      >
                        <div className="p-2 bg-red-500/20 rounded-lg">
                          <benefit.icon className="h-5 w-5 text-red-400" />
                        </div>
                        <p className="text-gray-300 flex-1">{benefit.title}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* CTA Section */}
                <div className="bg-gradient-to-r from-red-600/20 to-orange-600/20 rounded-2xl p-8 border border-red-500/30 text-center space-y-4">
                  <h3 className="text-2xl font-bold text-white">{t('appModal.joinWaitlist')}</h3>
                  <p className="text-gray-300">{t('appModal.joinDescription')}</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                    <button
                      onClick={scrollToWaitlist}
                      className="px-8 py-3 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-semibold rounded-xl transition-all transform hover:scale-105"
                    >
                      {t('appModal.notifyMe')}
                    </button>
                    <button
                      onClick={onClose}
                      className="px-8 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition-all border border-white/20"
                    >
                      {t('appModal.gotIt')}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}

export default AppDownloadModal
